import { CustomElement } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { FLEX_CONTAINER_COLUMN, POSITION } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { KEYBOARD_INPUT_ARROWS, KeyboardInput } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { getLogConsole, getLogService } from "@cervval.js/common/src/services/log.service";
import { setProp } from "@cervval.js/common/src/tools/any.tools";
import { getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import { CjsIconFa } from "../../icon/iconFa";
import { CjsSmartSelect, DefaultSmartSelectItem, SmartSelectOptions } from "../../smartSelect/smartSelect";
import { RenderCacheData } from "../../smartTable/smartTable";
import { AcceptInputValueType, CjsInput, InputOptions } from "../input";


/** Event dispatch on input search change event is triggered */
export type InputSearchChangeEvent<ValueType> = {
    /** New value */
    value?: ValueType
}

export type InputSearchOptions<ItemType, ValueType> = {

    /** Display a search icon after input */
    displaySearchIcon?: boolean,

    /** 
     * Format value in string format, to display it in input
     */
    formatValueDisplay: (item: ItemType) => ValueType
}

/** Parameters for InputSearch */
export const defaultInputSearchOptions: InputSearchOptions<DefaultSmartSelectItem, AcceptInputValueType> = {

    /** 
     * Format value in string format, to display it in input
     */
    formatValueDisplay: item => item.value
};

export interface InputSearchCommonOptions<ItemType, ItemValueType, ValueType> {

    /** Options of inputSearch */
    inputSearch: Partial<InputSearchOptions<ItemType, ValueType>>;

    /** Options of input */
    input: Partial<InputOptions<ValueType>>;

    /** Options of smartSelect */
    smartSelect: Partial<SmartSelectOptions<ItemType, ItemValueType>>;
}

/**
 * Input component with a value selector.
 * Use this component when user need to search for a specific value when is typing value.
 * ItemType: Type of data displayed in selector (ex: UserDTO, ActivityTypeDTO, ect.)
 * ItemValueType: Type of value for data in displayed selector (ex: ID, UID, string, number)
 * ValueType: Type of value of InputSearch component, should extends a string or number (ex: string, number, ID, UID, duration, etc.)
 */
@CustomElement({
    name: "cjs-input-search",
    style: require("./inputSearch.scss"),
    template: require("./inputSearch.html"),
    elementCssClasses: [FLEX_CONTAINER_COLUMN, POSITION.RELATIVE]
})
export class CjsInputSearch<ItemType, ItemValueType, ValueType extends AcceptInputValueType = AcceptInputValueType> extends WebComponent {

    @Event(NoBubblesEventInit)
    public changeEvent!: EventHandler<InputSearchChangeEvent<ValueType>>;

    /** Options for InputSearch */
    @TypedWebComponentOptions(defaultInputSearchOptions)
    public readonly options!: WebComponentOptions<InputSearchOptions<ItemType, ValueType>>;

    @TypedHtmlElements()
    public components!: {
        input: CjsInput<ValueType>,
        smartSelect: CjsSmartSelect<ItemType, ItemValueType>
    };

    /**
     * Get input
     */
    public get input(): CjsInput<ValueType> {
        return this.components.input;
    }

    /**
     * Get smartSelect
     */
    public get smartSelect(): CjsSmartSelect<ItemType, ItemValueType> {
        return this.components.smartSelect;
    }

    /** Shortcut to input options */
    public get inputOptions(): WebComponentOptions<InputOptions<ValueType>> {
        return this.input.options;
    }

    /** Shortcut to smartSelectOptions */
    public get smartSelectOptions(): WebComponentOptions<SmartSelectOptions<ItemType, ItemValueType>> {
        return this.smartSelect.options;
    }

    /** Store data of smartSelect before apply filter */
    private _dataBeforeFilter: ItemType[] = [];

    /** Is data in smartSelect is currently filtered */
    private _isFiltering = false;

    /** Timestamp of last select event (use to don't dispatch double event) */
    private _lastSelectEventTs?: number;

    constructor(options?: Partial<InputSearchCommonOptions<ItemType, ItemValueType, ValueType>>) {
        super();

        // Update options
        this.options.setWithoutRefresh(options?.inputSearch);
        this.inputOptions.setWithoutRefresh(options?.input);
        this.smartSelectOptions.setWithoutRefresh(options?.smartSelect);
    }

    /**
     * Init component after it is inserted in the dom
     */
    public override init(): void {

        // Hidden smartSelectContainer (! important: don't use display:none)
        this.smartSelect.components.smartSelectContainer.style.visibility = "hidden";
        this._initListeners();
    }

    /** Refresh component */
    public override refresh(): void {

        // Get searchIcon
        const searchIcon = this._getSearchIcon();

        // Refresh input options
        this.input.options.setAndRefreshIfChanged({
            inputSuffixText: this.options.displaySearchIcon ? searchIcon : undefined
        });

        // Refresh smartSelect options
        this.smartSelect.options.setAndRefreshIfChanged({
            selectedValue: undefined,
            filterDisabled: true,
            filterComparator: this.smartSelectOptions.filterComparator
        });
    }

    /** Get search value */
    public getSearchValue(): ValueType | undefined {
        return this.input.value;
    }

    /** Set search value */
    public setSearchValue(value: ValueType | undefined, p?: { refreshSearch?: boolean }): void {
        this.input.value = value;

        if (p?.refreshSearch !== false) {
            this._refreshSearchItems();
        }
    }

    /**
     * Clean the search input
     */
    public clear(): void {
        this.input.value = undefined;
    }

    /** 
     * Init all component listeners
     */
    private _initListeners(): void {
        this.input.inputEvent.on(_ => this._refreshSearchItems());

        this.input.addEventListener("keydown", e => {
            // Switch focus between input and select
            if (KEYBOARD_INPUT_ARROWS.includes(e.key as KeyboardInput)) {
                this.smartSelect.smartList.focus();
                // Create a fake keyboard event to dispatch selection on smartSelect
                const fakeKeyboardEvent = new CustomEvent("keydown");
                setProp(fakeKeyboardEvent, "key", e.key);
                // Dispatch new keyboard event on document to trigger smartSelect keydown listener
                document.dispatchEvent(fakeKeyboardEvent);
                e.preventDefault();
            } else if (e.key === KeyboardInput.ENTER) {
                this.changeEvent.emit({ value: this.getSearchValue() });
            } else {
                this.input.focus();
            }
        });

        // Dispatch change event
        this.input.changeEvent.on(e => {
            const debounceTs = (this._lastSelectEventTs ?? 0) - e.timeStamp;// Debounce smartSelect select event to don't dispatch double event
            const debounceLimit = 100;// ms
            if (!this.smartSelect.isOpen() && (!this._lastSelectEventTs || debounceTs > debounceLimit)) {
                this.changeEvent.emit({ value: this.getSearchValue() });
            } else {
                this.input.focus();
            }
            this._lastSelectEventTs = undefined;
        });

        // Listen on first select open to install keydown listener
        this.smartSelect.openEvent.on(() => {
            // Switch focus between input and select
            this.smartSelect.smartList?.addEventListener("keydown", e => {
                if (KEYBOARD_INPUT_ARROWS.includes(e.key as KeyboardInput)) {
                    e.preventDefault();
                    this.smartSelect.smartList.focus();
                } else {
                    this.input.focus();
                }
            });
        }, { once: true });

        // On smartSelect closing
        this.smartSelect.closeEvent.on(() => {
            if (this._isFiltering) {
                // Restore data
                this.smartSelect.options.setWithoutRefresh({
                    data: this._dataBeforeFilter
                });
            }
            // Update state
            this._isFiltering = false;
        });

        // When smartSelect value has changed
        this.smartSelect.selectEvent.on(e => {
            this._lastSelectEventTs = e.timeStamp;
            this._updateInputFromSelectValue(e.detail.item ?? undefined);
            this.changeEvent.emit({ value: this.getSearchValue() });
        });

    }

    /** Called when input value has changed, displayed items should be refreshed */
    private _refreshSearchItems(): void {
        const filterComparator = this.smartSelectOptions.filterComparator;
        const sortFilter = this.smartSelectOptions.sortFilter;
        // If filtering still occuring, use base data
        const datas = this._isFiltering ? this._dataBeforeFilter : getVOFValue(this.smartSelect.options.data);

        // Useless to open smartSelect if comparator not given or data is empty
        if (!filterComparator) { getLogConsole().warn("'filterComparator' option not given for InputSearch component"); }
        if (!filterComparator || !datas) { return; }

        // Update state
        this._isFiltering = true;

        // Keep data before filter
        this._dataBeforeFilter = datas;

        // Filter datas with filterComparator
        const renderCacheData: RenderCacheData = {};
        const newData = datas.filter(data => {
            if (this.input.value == null || this.input.value === "") { return true; }
            return filterComparator({ item: data, filterValue: String(this.input.value), itemValue: data }, renderCacheData);
        });

        if (sortFilter) { newData.sort((a, b) => sortFilter(a, b, String(this.input.value ?? ""))); }

        // Retrieve current selected item to keep selected item in smartSelect when open
        const previousSelectedItem = this.smartSelect.smartList.displayableRows.find(row => row.selected);
        const previousSelectedItemData = getVOFValue(previousSelectedItem?.data);

        // Update options
        this.smartSelect.options.setAndRefreshIfChanged({
            data: newData,
            selectedValue: previousSelectedItemData ? this.smartSelect.getItemValue(previousSelectedItemData) : undefined
        });

        // Open smartSelect if needed
        if (newData.length && !this.smartSelect.isOpen()) {
            this.smartSelect.open();
        }
    }

    /** Update input from current value */
    private _updateInputFromSelectValue(item?: ItemType): void {
        if (this.smartSelect.options.selectedValue && item) {
            if (!this.options.formatValueDisplay) {
                getLogService().error("Format value display not defined");
                return;
            }
            this.smartSelect.close();
            this.input.value = this.options.formatValueDisplay(item) as ValueType;// Will dispatch a value change
        }
    }


    /** Get search icon for input */
    private _getSearchIcon(): CjsIconFa {
        return new CjsIconFa({
            name: "fa-magnifying-glass"
        });
    }
}